/**
 * Guardar el respaldo en tu nube, desde los ajustes.
 *
 * El botón entrega el archivo al **menú de compartir del teléfono**: desde ahí se puede
 * mandar a Google Drive, OneDrive, Dropbox, al correo o a donde se quiera.
 *
 * Se explica en la pantalla lo que esto es y lo que no:
 *
 *  · **Es**: una forma de sacar la copia del servidor y ponerla donde tú decidas, sin
 *    darle a nadie acceso a tus finanzas.
 *  · **No es**: una copia automática. Hay que pulsar el botón. Tener copia sola cada
 *    noche necesita registrar una aplicación con Google o Microsoft, y eso es una
 *    decisión aparte.
 *
 * Decir las dos cosas es lo que hace que el botón no engañe.
 */

import { Check, CloudUpload, Share2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { canShareFiles, fetchBackupFile, shareBackup } from '../lib/cloud-backup';
import { Note } from './ui';

export function CloudBackup(): React.JSX.Element {
  const [supported, setSupported] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  /*
   * Se pregunta al dispositivo con un archivo de prueba en la mano.
   *
   * No basta con ver si existe `navigator.share`: muchos navegadores lo tienen para
   * texto y enlaces pero no aceptan archivos. Ofrecer el botón sin comprobarlo sería
   * poner algo que al pulsarlo no lleva a ninguna parte.
   */
  useEffect(() => {
    const probe = new File(['{}'], 'prueba.json', { type: 'application/json' });
    setSupported(canShareFiles(probe));
  }, []);

  const share = async (): Promise<void> => {
    setBusy(true);
    setMessage(null);

    try {
      const file = await fetchBackupFile();
      const done = await shareBackup(file);

      if (done) {
        setMessage({
          tone: 'ok',
          text: 'Listo. Elige dónde guardarlo y quedará fuera de este servidor.',
        });
      }
      // Si el usuario cierra el menú no se dice nada: no ha pasado nada malo.
    } catch {
      setMessage({
        tone: 'bad',
        text: 'No se pudo preparar el respaldo. Prueba con el botón de descargar.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {supported === true && (
        <>
          <button
            type="button"
            className="ed-btn-secondary w-full"
            disabled={busy}
            onClick={() => void share()}
          >
            <CloudUpload size={16} aria-hidden="true" />
            {busy ? 'Preparando…' : 'Guardar en tu nube'}
          </button>

          <p className="text-xs text-ed-text-muted">
            Se abre el menú de tu teléfono: desde ahí puedes mandarlo a Google Drive, a
            OneDrive, a tu correo o donde quieras. <strong>Ninguna aplicación de terceros
            tiene acceso a tus datos</strong>; solo se le entrega el archivo a tu sistema,
            y tú decides.
          </p>
        </>
      )}

      {supported === false && (
        <p className="text-xs text-ed-text-muted">
          Este navegador no puede compartir archivos directamente. Usa el botón de
          descargar de arriba: el archivo queda en tu teléfono y desde ahí lo puedes subir
          a donde quieras.
        </p>
      )}

      {supported === null && (
        <p className="text-xs text-ed-text-muted">Comprobando qué puede hacer este dispositivo…</p>
      )}

      {message && (
        <p
          role="status"
          className={`flex items-start gap-2 rounded-ed px-3 py-2 text-xs ${
            message.tone === 'ok'
              ? 'border border-ed-success/40 bg-ed-success/10 text-ed-success'
              : 'border border-ed-coral-deep/40 bg-ed-coral-deep/10 text-ed-coral-light'
          }`}
        >
          {message.tone === 'ok' && <Check size={13} className="mt-0.5 shrink-0" aria-hidden="true" />}
          <span>{message.text}</span>
        </p>
      )}

      <Note>
        Esto <strong>no es una copia automática</strong>: hay que pulsar el botón. Tener
        una copia sola cada noche necesita registrar una aplicación con Google o
        Microsoft, y eso requiere tu cuenta. Mientras tanto, el respaldo del servidor
        —cifrado y con restauración comprobada— sigue haciéndose todos los días.
        <Share2 size={12} className="ml-1 inline" aria-hidden="true" />
      </Note>
    </div>
  );
}
